import * as React from "react"
import { MinimumWageBasicConfigurationModel } from "@/app/models/configuration/minimumWage/minimumWageBasicConfigurationModel"
import { FieldsetSection } from "../components/FieldsetSection";
import { RowCheckbox } from "../components/RowCheckbox";
import { useLocalize } from "@/modules/shared/context/localize/LocalizeContext";

const defaultFields: MinimumWagePolishOptionsSectionFieldName[] = [
    "includeAdditionForOvertimeFromContract",
    "includeAdditionForWorkOnHolidaysFromContract",
    "includeAdditionForDutyFromContract",
    "includeAdditionForNightTimeWorkFromContract",
    "includeBounsFromContract",
    "includeEuWorkAddition",
]

export type MinimumWagePolishOptionsSectionFieldName =
    | "includeAdditionForOvertimeFromContract"
    | "includeAdditionForWorkOnHolidaysFromContract"
    | "includeAdditionForDutyFromContract"
    | "includeAdditionForNightTimeWorkFromContract"
    | "includeBounsFromContract"
    | "includeEuWorkAddition"

type Props = {
    className?: string
    loading?: boolean
    fields?: MinimumWagePolishOptionsSectionFieldName[]
    state: { configuration: MinimumWageBasicConfigurationModel }
    setState: React.Dispatch<React.SetStateAction<{ configuration: MinimumWageBasicConfigurationModel }>>
}

export const MinimumWagePolishOptionsSection = ({
    className,
    loading,
    fields = defaultFields,
    state,
    setState,
}: Props) => {
    const { t } = useLocalize()

    const updateValue = React.useCallback((name: string, value: any) => {
        setState((s) => ({
            ...s,
            configuration: {
                ...s.configuration,
                [name]: value,
            },
        }))
    }, [setState])

    if (loading) {
        return <p>Loading...</p>
    }

    const basicConfiguration = state.configuration

    const readOnly =
        state != null
            ? !state.configuration.calculateAmountsForCountry
            : false;

    return (
        <div className={className} data-testid="minimum-wage-polish-options-section">
            <FieldsetSection title={t('configuration.minimumWages.includeAdditionsFromContract')}>
                {/* includeAdditionForOvertimeFromContract */}
                {fields.includes('includeAdditionForOvertimeFromContract') && (
                    <RowCheckbox
                        label={t('configuration.minimumWages.includeAdditionsForOvertimeFromContract')}
                        id="includeAdditionForOvertimeFromContract"
                        name="includeAdditionForOvertimeFromContract"
                        checked={basicConfiguration?.includeAdditionForOvertimeFromContract}
                        readOnly={readOnly}
                        setValue={(value) => updateValue('includeAdditionForOvertimeFromContract', value)}
                    />
                )}

                {/* includeAdditionForWorkOnHolidaysFromContract */}
                {fields.includes('includeAdditionForWorkOnHolidaysFromContract') && (
                    <RowCheckbox
                        label={t('configuration.minimumWages.includeAdditionForWorkOnHolidaysFromContract')}
                        id="includeAdditionForWorkOnHolidaysFromContract"
                        name="includeAdditionForWorkOnHolidaysFromContract"
                        checked={basicConfiguration?.includeAdditionForWorkOnHolidaysFromContract}
                        readOnly={readOnly}
                        setValue={(value) => updateValue('includeAdditionForWorkOnHolidaysFromContract', value)}
                    />
                )}

                {/* includeAdditionForDutyFromContract */}
                {fields.includes('includeAdditionForDutyFromContract') && (
                    <RowCheckbox
                        label={t('configuration.minimumWages.includeAdditionForDutyFromContract')}
                        id="includeAdditionForDutyFromContract"
                        name="includeAdditionForDutyFromContract"
                        checked={basicConfiguration?.includeAdditionForDutyFromContract}
                        readOnly={readOnly}
                        setValue={(value) => updateValue('includeAdditionForDutyFromContract', value)}
                    />
                )}

                {/* includeAdditionForNightTimeWorkFromContract */}
                {fields.includes('includeAdditionForNightTimeWorkFromContract') && (
                    <RowCheckbox
                        label={t('configuration.minimumWages.includeAdditionForNightTimeWorkFromContract')}
                        id="includeAdditionForNightTimeWorkFromContract"
                        name="includeAdditionForNightTimeWorkFromContract"
                        checked={basicConfiguration?.includeAdditionForNightTimeWorkFromContract}
                        readOnly={readOnly}
                        setValue={(value) => updateValue('includeAdditionForNightTimeWorkFromContract', value)}
                    />
                )}

                {/* includeBounsFromContract (keeping the original key) */}
                {fields.includes('includeBounsFromContract') && (
                    <RowCheckbox
                        label={t('configuration.minimumWages.includeBounsFromContract')}
                        id="includeBounsFromContract"
                        name="includeBounsFromContract"
                        checked={basicConfiguration?.includeBounsFromContract}
                        readOnly={readOnly}
                        setValue={(value) => updateValue('includeBounsFromContract', value)}
                    />
                )}

                {/* includeEuWorkAddition */}
                {fields.includes('includeEuWorkAddition') && (
                    <RowCheckbox
                        label={t('configuration.minimumWages.includeEuWorkAddition')}
                        id="includeEuWorkAddition"
                        name="includeEuWorkAddition"
                        checked={basicConfiguration?.includeEuWorkAddition}
                        readOnly={readOnly}
                        setValue={(value) => updateValue('includeEuWorkAddition', value)}
                    />
                )}
            </FieldsetSection>
        </div>
    )
}
